import { useCallback, useEffect } from 'react';
import { useUIStore, useGameStore } from '@/stores';
import { Team } from '@/types/player';
import './GameOverModal.css';

/**
 * GameOverModal - ゲーム終了モーダル
 * 勝敗結果を表示し、再プレイまたはメインメニューへの遷移を提供
 */
const GameOverModal = () => {
  const { isOpen, winner } = useUIStore((state) => state.modals.gameOver);
  const closeGameOver = useUIStore((state) => state.closeGameOver);
  const navigateTo = useUIStore((state) => state.navigateTo);
  const resetGame = useGameStore((state) => state.resetGame);
  const localPlayerId = useGameStore((state) => state.localPlayerId);
  const gameState = useGameStore((state) => state.gameState);

  // ローカルプレイヤーのチームを取得
  const localPlayer = gameState?.players.find(p => p.id === localPlayerId);
  const localTeam = localPlayer?.team;

  // 勝利かどうか
  const isVictory = winner && localTeam && winner === localTeam;

  // ESCキーで閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleMainMenu();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // 再プレイ
  // プロトタイプ版ではセットアップ画面を経由せず、メインメニューに戻して
  // チームを再選択する流れに統一する
  // (ref: claudedocs/prototype-titlescreen-plan.md §2 ゲーム終了後の遷移先)
  const handlePlayAgain = useCallback(() => {
    closeGameOver();
    resetGame();
    navigateTo('main_menu');
  }, [closeGameOver, resetGame, navigateTo]);

  // メインメニューへ
  const handleMainMenu = useCallback(() => {
    closeGameOver();
    resetGame();
    navigateTo('main_menu');
  }, [closeGameOver, resetGame, navigateTo]);

  // チーム名を取得
  const getTeamName = (team: Team): string => {
    return team === Team.TEAM_1 ? 'チーム1' : 'チーム2';
  };

  if (!isOpen || !winner) {
    return null;
  }

  return (
    <div className="GameOverModal__overlay">
      <div className={`GameOverModal__content ${isVictory ? 'GameOverModal__content--victory' : 'GameOverModal__content--defeat'}`}>
        {/* タイトル */}
        <h1 className="GameOverModal__title">
          <span className="GameOverModal__eyebrow">
            {isVictory ? 'OPERATION COMPLETE' : 'OPERATION FAILED'}
          </span>
          <span className="GameOverModal__title-text">
            {isVictory ? '勝利' : '敗北'}
          </span>
        </h1>

        {/* 結果メッセージ */}
        <p className="GameOverModal__message">
          {getTeamName(winner)}の勝利です
        </p>

        {/* アクションボタン */}
        <div className="GameOverModal__actions">
          <button
            className="GameOverModal__button GameOverModal__button--primary"
            onClick={handlePlayAgain}
          >
            もう一度プレイ
          </button>
          <button
            className="GameOverModal__button GameOverModal__button--secondary"
            onClick={handleMainMenu}
          >
            メインメニュー
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOverModal;
